import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, SearchInput } from '@ds';
import { MdOutlineAdd } from 'react-icons/md';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import {
  HospitalFilters,
  getRenderHospitalTableCell,
  HOSPITALS_LIST_FILTERS_BASE,
  HOSPITAL_TABLE_COLUMNS,
} from '@lp/features/admin/tables/HospitalListTable';
import { AuthContext } from '@lp/core/auth';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useHospitalListQuery } from '@/queries/hospital';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { useWrappedRemoveHospitalMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';

export const HospitalsList = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: HOSPITALS_LIST_FILTERS_BASE,
    query: useHospitalListQuery,
  });

  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.HospitalCreate);

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useWrappedRemoveHospitalMutation,
    successMessage: 'Donor Hospital deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate('/admin/hospitals/locations/create');
  };

  const handleEdit = useCallback(
    (hospitalId: string) => {
      navigate(`/admin/hospitals/locations/${hospitalId}`);
    },
    [navigate],
  );

  const { resolveSubdivisionName } = useCountriesData();
  const renderHospitalTableCell = useMemo(() => {
    return getRenderHospitalTableCell(handleEdit, onModalOpen, resolveSubdivisionName);
  }, [handleEdit, onModalOpen, resolveSubdivisionName]);

  const HOSPITAL_TABLE_COLUMNS_FILTERED = useMemo(() => {
    return hasCreatePermissions
      ? HOSPITAL_TABLE_COLUMNS
      : HOSPITAL_TABLE_COLUMNS.filter((item) => item.key !== 'actions');
  }, [hasCreatePermissions]);

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title="Donor Hospital Locations">
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>
          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>
        <div className="flex flex-col gap-4">
          <HospitalFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
          <DataTable
            label="Donor Hospital Locations"
            columns={HOSPITAL_TABLE_COLUMNS_FILTERED}
            renderCell={renderHospitalTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>
      <DeleteModal
        title="Delete Donor Hospital?"
        description="Are you sure you want to delete the Donor Hospital?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
